import { SkyBadge, SkyLink, SkyText, SkyTooltip } from "@skypointcloud/skypoint-ui";
import type { ConnectionReliability, Reliability } from "@shared/types/reliability";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { RELIABILITY_HELP, helpHref } from "@/lib/helpLinks";
import { formatInstant } from "@/lib/time/displayTime";

/**
 * Level B (#828): a connection's last load and freshness, and a success rate, as Connections,
 * Schedules, the Overview and Your data show them. Every instant through `formatInstant` in the
 * person's display zone; the words say what the numbers are, never a promise (owner decision 2:
 * internal targets only).
 */

/** The reliability row of one connection, or null while it loads or when the read failed. */
export function connectionReliabilityOf(
  data: Reliability | undefined,
  dataflowId: string,
): ConnectionReliability | null {
  return data?.connections.find((c) => c.dataflowId === dataflowId) ?? null;
}

/** `0.875` is "88%"; no runs is "No runs". */
export function successRateText(rate: number | null): string {
  return rate === null ? "No runs" : `${Math.round(rate * 100)}%`;
}

/**
 * The `Late` badge, shown only when the connection is late. When it was due is its tooltip, on
 * hover and on keyboard focus, and the same sentence for a screen reader, which never sees a
 * tooltip (the Schedules State cell's pattern).
 */
export function LateBadge({
  connection,
  testId,
}: {
  connection: ConnectionReliability | null;
  testId: string;
}) {
  const zone = useDisplayTz();
  if (connection?.freshness !== "late") return null;
  const hint = connection.expectedBy
    ? `Late: expected by ${formatInstant(connection.expectedBy, zone, "short", { named: true })}`
    : "Late";
  return (
    <SkyTooltip content={hint} testId={`${testId}-hint`}>
      <span tabIndex={0} className="inline-flex" data-testid={testId}>
        <span aria-hidden="true">
          <SkyBadge appearance="tint" color="warning">
            Late
          </SkyBadge>
        </span>
        <span className="sr-only">{hint}</span>
      </span>
    </SkyTooltip>
  );
}

/** "Last loaded <when>", or "Not loaded yet" for a connection that never landed a table. */
export function lastLoadedText(connection: ConnectionReliability, zone: string): string {
  return connection.lastSuccessAt
    ? `Last loaded ${formatInstant(connection.lastSuccessAt, zone, "short", { named: true })}`
    : "Not loaded yet";
}

/** The muted "Last loaded" line under a connection's last import. */
export function LastLoadedLine({
  connection,
  testId,
}: {
  connection: ConnectionReliability | null;
  testId: string;
}) {
  const zone = useDisplayTz();
  if (!connection) return null;
  return (
    <SkyText size={200} color="muted" block testId={testId}>
      {lastLoadedText(connection, zone)}
    </SkyText>
  );
}

/** "What these numbers mean": the reliability guide's section for the screen. */
export function ReliabilityHelpLink({
  screen,
  testId,
}: {
  screen: keyof typeof RELIABILITY_HELP;
  testId: string;
}) {
  const target = RELIABILITY_HELP[screen];
  return (
    <SkyLink
      href={helpHref(target.page, target.section)}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      testId={testId}
    >
      What these numbers mean
    </SkyLink>
  );
}
